import {useQuery} from "@apollo/client";
import isEmpty from "lodash/isEmpty";
import union from "lodash/union";
import pluralize from "pluralize";
import {useEffect, useMemo, useState} from "react";
import {useDebounce} from "use-debounce";

import {Button} from "@/components/button/Button.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {ListPanelSelect, ListPanelSelectOption} from "@/components/listPanelSelect/ListPanelSelect.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {GQL_STATUSPAGE_COMPONENTS, StatusPageComponentsListResponse} from "@/lib/queries/statuspages.ts";
import {Component} from "@/lib/types/statuspages.ts";

import styles from "./MultiStatusPageComponentSelect.module.scss";

interface MultiStatusPageComponentSelectProps {
  className?: string;
  statusPageId: number;
  defaultValue?: number[];
  onAppend: (value: number) => void;
  onReplace: (values: number[]) => void;
  defaultComponents?: Component[];
}

const COMPONENT_LIMIT = 200;

/*
form context-less component selector for status page component groups.
Replace - replaces all selected components with new ones,
Append - adds new components to the existing ones.
 */
export const MultiStatusPageComponentSelect = ({
  className,
  statusPageId,
  defaultValue,
  onAppend,
  onReplace,
  defaultComponents,
}: MultiStatusPageComponentSelectProps) => {
  const [search, setSearch] = useState("");
  const [searchValue] = useDebounce(search, 300);
  const [selectedComponents, setSelectedComponents] = useState<number[]>(defaultValue || []);
  const [availableComponents, setAvailableComponents] = useState<number[]>([]);
  const [filteredSelectedComponents, setFilteredSelectedComponents] = useState<number[]>([]);

  const {data, previousData, loading} = useQuery<StatusPageComponentsListResponse>(GQL_STATUSPAGE_COMPONENTS, {
    variables: {
      statusPageId,
      search: searchValue,
      excludeGroups: true,
    },
  });

  const allComponents = useMemo(
    () => data?.response?.items || previousData?.response?.items || defaultComponents || [],
    [data?.response?.items, previousData?.response?.items, defaultComponents]
  );

  useEffect(() => {
    const availableComponentIds = allComponents.map((c) => c.id);

    const newAvailableComponents = availableComponentIds.filter((id) => !selectedComponents.includes(id));
    setAvailableComponents(newAvailableComponents);

    let filteredSelected = selectedComponents;
    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredSelected = selectedComponents.filter((id) => {
        const component = allComponents.find((c) => c.id === id);
        if (!component) return false;
        return (
          component.name.toLowerCase().includes(searchLower) ||
          component.description?.toLowerCase().includes(searchLower) ||
          component.service_name?.toLowerCase().includes(searchLower)
        );
      });
    }
    setFilteredSelectedComponents(filteredSelected);
  }, [searchValue, allComponents, selectedComponents]);

  const getListPanelValues = (filterArray: number[]) =>
    allComponents.reduce((acc, {id, name, service_name, service_type}) => {
      if (filterArray.includes(id)) {
        acc.push({
          value: id,
          title: name,
          subtitle: service_name || "No service linked",
          tag: service_type,
        });
      }
      return acc;
    }, [] as ListPanelSelectOption[]);

  if (loading && !previousData && isEmpty(defaultComponents)) {
    return <LoadingContainer text="Loading Components..." />;
  }

  return (
    <div className={className}>
      <div>
        <FormLabel
          forId="multi-component-search"
          helpText="Select the Components you would like to include in this group"
        >
          Manually Select Components
        </FormLabel>
        <SearchInput onChange={setSearch} id="multi-component-search" />
      </div>
      <Divider className="my-3" />
      <div className="d-flex gap-3 flex-column flex-sm-row">
        <div className={styles.container}>
          <FormLabel forId="components-available" helpText="Click on Components to add" className="mb-2">
            Components Available
          </FormLabel>
          <div className={styles.listContainer}>
            <ListPanelSelect
              id="components-available"
              className={styles.listPanel}
              noOptionsText="No Components Available"
              onChange={(items) => {
                const elToAdd = items.find(({value}) => !selectedComponents.includes(value))!.value;
                setSelectedComponents([...selectedComponents, elToAdd]);
                setAvailableComponents(availableComponents.filter((c) => c !== elToAdd));
                onAppend(elToAdd);
              }}
              options={getListPanelValues(availableComponents)}
              value={getListPanelValues(selectedComponents)}
              hideTag
            />
          </div>
          <Button
            variant="linkBlue"
            size="fitContent"
            typography="linkBold"
            onClick={() => {
              const selected = union(selectedComponents, availableComponents);
              setSelectedComponents(selected);
              setAvailableComponents([]);
              onReplace(selected);
            }}
          >
            Select All
          </Button>
        </div>
        <div className={styles.container}>
          <FormLabel forId="components-selected" helpText="Click on Components to remove" className="mb-2">
            {`${pluralize("Component", selectedComponents.length, true)} Selected`}
          </FormLabel>
          <div className={styles.listContainer}>
            <ListPanelSelect
              id="components-selected"
              className={styles.listPanel}
              noOptionsText="No Components Selected"
              onChange={([{value}]) => {
                const elToRemove = selectedComponents.find((el) => el === value)!;
                const newSelectedComponents = selectedComponents.filter((c) => c !== elToRemove);
                setSelectedComponents(newSelectedComponents);
                setAvailableComponents([...availableComponents, elToRemove]);
                onReplace(newSelectedComponents);
              }}
              options={getListPanelValues(filteredSelectedComponents)}
              value={[]}
              hideTag
            />
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="linkBlue"
              size="fitContent"
              typography="linkBold"
              onClick={() => {
                const selected = union(selectedComponents, availableComponents);
                setAvailableComponents(selected);
                setSelectedComponents([]);
                onReplace([]);
              }}
            >
              Remove All
            </Button>
            <div className="pulseTextBodySmall pulse-color-gray-700">Max. {COMPONENT_LIMIT} Components</div>
          </div>
        </div>
      </div>
    </div>
  );
};
